// Module 09 §9 — the pure widget → module-08 mapping. No Vue, no store, no http: every reader
// below takes the persisted bag and an explicit context, which is what makes the whole seam
// testable without mounting a component or stubbing a request.
//
// Two rules this file inherits from ORP-18656 and must not weaken:
//   • It READS the wire bag, it never rewrites it. Nothing here feeds `serializeLayout`, so a key
//     it does not understand is simply not mapped — it is never dropped from the persisted object.
//   • `kind` is the read-side discriminator (`deriveWidgetKind` already normalised the legacy
//     `{visibleType:'table', selectedTable:'Matrix'}` split). Mapping keys off `kind`, never off
//     `visibleType`, is what keeps that normalisation in one place.
//
// The wire bag is an index-signature record, so every field is `unknown` until it is narrowed.
// The `str`/`strList`/`num` readers below are that narrowing, and they are deliberately lenient in
// the same places production is messy (a metric list persisted as `null`, a `rowCount` persisted
// as a string) — a widget with one malformed key still renders.

import type { DashboardWidget, DashboardWidgetWire, WidgetKind } from '../models/widget.types'

import { widgetDefaultPostData } from './widgetDateSpec'

import type {
  ChartType,
  DataVisibleCardMediaType,
  DataVisiblePaginationConfig,
  ReportFilter,
  ReportSpec,
  ReportTableType,
  ReportViewType,
} from '@/features/reports'

/** Kinds `ReportWidget` renders by handing props to module 08's `DataVisible`. */
export const REPORT_WIDGET_KINDS = ['table', 'matrix', 'chart', 'card', 'double'] as const
export type ReportWidgetKind = (typeof REPORT_WIDGET_KINDS)[number]

export {
  BUILDER_OWNED_WIDGET_KEYS,
  BUILDER_SUPPORTED_WIDGET_KINDS,
  isWidgetBuilderEditable,
  widgetFromForm,
  widgetToBuilderForm,
  type WidgetBuilderForm,
} from './widgetBuilderSpec'
export {
  WIDGET_COMPARE_MODE_IDS,
  WIDGET_DATE_PRESET_IDS,
  widgetDatePickerHydration,
  widgetDatePreset,
  widgetDefaultPostData,
} from './widgetDateSpec'

const REPORT_WIDGET_KIND_SET: ReadonlySet<string> = new Set(REPORT_WIDGET_KINDS)

export function isReportWidgetKind(kind: WidgetKind | null): kind is ReportWidgetKind {
  return kind !== null && REPORT_WIDGET_KIND_SET.has(kind)
}

/**
 * Everything a widget needs from outside its own persisted bag.
 *
 * `targetCurrency` arrives here rather than being injected, because `service/**` is pure — the
 * host reads `accountCenterSelectedCurrency` from the account-center provide and passes it down.
 */
export interface WidgetRenderContext {
  /** The `&date_start=…` fragment the per-widget date picker emits. */
  defaultPostData?: string
  /** Workspace the dashboard is being viewed for; the fallback when the widget names none. */
  wsId?: number | string | null
  /** Account-center workspace set supplied by the dashboard host. */
  accountWorkspaces?: readonly (number | string)[]
  /** Account-center currency override (08's `useReportCellFormatter` reads the same value). */
  targetCurrency?: string | null
  /** Disambiguates the DataVisible instance id when one widget is mounted more than once. */
  idPrefix?: string
}

/**
 * The module-08 `DataVisible` prop subset a report widget mounts, including the two account-center
 * values (`workspaceIds`, `targetCurrency`) that §16 asks every report surface to honour.
 *
 * `DataVisible` accepts both since ORP-19956, so the whole object binds straight through — the
 * same scope `ScoreWidget`/`ListTableWidget` apply via their own `useReportData`.
 */
export interface WidgetReportProps {
  id: string
  reportId: number
  partitionName?: string
  dimensions: string[]
  metrics: string[]
  chartDimensions?: string[]
  chartMetrics?: string[]
  filters: ReportFilter[]
  view: ReportViewType
  allowedViews: ReportViewType[]
  chartType?: ChartType
  tableType?: ReportTableType
  sortField?: string
  sortOrder?: 1 | -1
  breakdown: string | null
  matrixDimension: string | null
  pivotMetric: string | null
  pagination: DataVisiblePaginationConfig | false
  partitionOnly?: boolean
  edges?: string[]
  cardMediaType?: DataVisibleCardMediaType
  defaultPostData: string
  workspaceIds: (number | string)[]
  targetCurrency?: string
}

interface ViewMapping {
  view: ReportViewType
  allowedViews: ReportViewType[]
  tableType?: ReportTableType
}

/**
 * Which 08 view each report kind opens on.
 *
 * `double` is the only kind that opens on one view and offers the other — it is legacy's
 * "chart + table" block, and 08's toolbar owns the toggle. `matrix` is a table whose presenter is
 * `matrix_table`; the registry key is not `matrix` (contract §3.2 amendment 3).
 */
const VIEW_BY_KIND: Readonly<Record<ReportWidgetKind, ViewMapping>> = {
  table: { view: 'table', allowedViews: ['table'], tableType: 'table' },
  matrix: { view: 'table', allowedViews: ['table'], tableType: 'matrix_table' },
  chart: { view: 'chart', allowedViews: ['chart'] },
  double: { view: 'chart', allowedViews: ['table', 'chart'] },
  card: { view: 'card', allowedViews: ['card'] },
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function strList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
}

function num(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** `-1` (descending) is legacy's default for every dashboard block that does not persist one. */
function sortOrder(value: unknown): 1 | -1 {
  return num(value) === 1 ? 1 : -1
}

/**
 * Filters are 08's model; a persisted entry that is not an object is dropped rather than sent.
 * Exported so a renderer that adds its own ad-hoc filters (e.g. `OverviewChartWidget`'s
 * `ReportFilters` search box) can merge them onto the widget's configured set instead of
 * silently overriding it — `widgetToReportSpec`'s own `filters` default is this same call.
 */
export function widgetDefaultFilters(wire: DashboardWidgetWire): ReportFilter[] {
  if (!Array.isArray(wire.defaultFilters)) return []
  return wire.defaultFilters.filter((entry): entry is ReportFilter => record(entry) !== null)
}

/** Ports `col?.reportId || col?.selectedLevel.report_id` — `selectedLevel` is an unnamed live key. */
export function widgetReportId(wire: DashboardWidgetWire): number | undefined {
  return num(wire.reportId) ?? num(record(wire.selectedLevel)?.report_id)
}

/** Ports `col?.partition || col.selectedLevel?.partition_name`. */
export function widgetPartition(wire: DashboardWidgetWire): string | undefined {
  return str(wire.partition) ?? str(record(wire.selectedLevel)?.partition_name)
}

/**
 * The workspaces a widget reads. An account-center widget names its own set; everything else
 * falls back to the dashboard's workspace. Legacy read `props.accountWorkspaces` while testing
 * `props.section.accountWorkspaces` — the prop was never declared on either data widget, so the
 * account-center path silently sent `undefined`. Reading one source fixes that.
 */
export function widgetWorkspaceIds(
  wire: DashboardWidgetWire,
  ctx: WidgetRenderContext = {},
): (number | string)[] {
  const own = wire.accountWorkspaces
  if (Array.isArray(own) && own.length > 0) {
    return own.filter(
      (id): id is number | string => typeof id === 'number' || typeof id === 'string',
    )
  }
  if (ctx.accountWorkspaces && ctx.accountWorkspaces.length > 0) {
    return ctx.accountWorkspaces.filter(
      (id): id is number | string => typeof id === 'number' || typeof id === 'string',
    )
  }
  return ctx.wsId === null || ctx.wsId === undefined ? [] : [ctx.wsId]
}

/** Account-center currency override → an ISO code. Ports the `code || value` shape legacy read. */
export function resolveTargetCurrency(value: unknown): string | undefined {
  if (typeof value === 'string') return str(value)
  const holder = record(value)
  if (!holder) return undefined
  return str(holder.code) ?? str(holder.value)
}

/**
 * Map a widget to the props its module-08 composite mounts with.
 *
 * Returns `null` when the widget cannot address a report at all (no `report_id`) or is not a
 * report kind — the host renders the unavailable placeholder rather than mounting `DataVisible`
 * with a bad request.
 */
export function widgetToReportProps(
  widget: DashboardWidget,
  ctx: WidgetRenderContext = {},
): WidgetReportProps | null {
  if (!isReportWidgetKind(widget.kind)) return null
  const wire = widget.wire
  const reportId = widgetReportId(wire)
  if (reportId === undefined) return null

  const mapping = VIEW_BY_KIND[widget.kind]
  const partitionName = widgetPartition(wire)
  const sortField = str(wire.sortField)
  const chartType = str(wire.defaultChart)
  const rowCount = num(wire.rowCount)
  const targetCurrency = str(ctx.targetCurrency)

  return {
    id: `${ctx.idPrefix ?? 'dashboard-widget'}-${str(wire.id) ?? String(reportId)}`,
    reportId,
    ...(partitionName ? { partitionName } : {}),
    dimensions: strList(wire.setDimensions),
    metrics: strList(wire.setMetrics),
    ...(widget.kind === 'table' || widget.kind === 'matrix'
      ? {}
      : {
          chartDimensions: strList(wire.chartDimensions),
          chartMetrics: strList(wire.chartMetrics),
        }),
    filters: widgetDefaultFilters(wire),
    view: mapping.view,
    allowedViews: [...mapping.allowedViews],
    ...(mapping.tableType ? { tableType: mapping.tableType } : {}),
    ...(chartType ? { chartType: chartType as ChartType } : {}),
    ...(sortField ? { sortField, sortOrder: sortOrder(wire.sortOrder) } : {}),
    breakdown: str(wire.defaultBreakdown) ?? null,
    matrixDimension: str(wire.matrixDimension) ?? null,
    pivotMetric: str(wire.previewMetric) ?? null,
    // Legacy dashboard blocks are unpaged unless the block persisted an explicit row count.
    pagination: rowCount === undefined ? false : { rows: rowCount, first: 0 },
    ...(wire.partition_only === true ? { partitionOnly: true } : {}),
    ...(strList(wire.edges).length > 0 ? { edges: strList(wire.edges) } : {}),
    ...(widget.kind === 'card' ? { cardMediaType: cardMediaType(wire) } : {}),
    defaultPostData: ctx.defaultPostData ?? widgetDefaultPostData(wire),
    workspaceIds: widgetWorkspaceIds(wire, ctx),
    ...(targetCurrency ? { targetCurrency } : {}),
  }
}

/** Media cards persist their kind on `mediaColumns`/`cardMediaType`; `Image` is legacy's default. */
function cardMediaType(wire: DashboardWidgetWire): DataVisibleCardMediaType {
  const declared = str(wire.cardMediaType) ?? str(wire.mediaType)
  return declared === 'Video' ? 'Video' : 'Image'
}

/**
 * The request `ScoreWidget` and `ListTableWidget` issue directly. They read 08's data layer rather
 * than mounting a composite, so unlike {@link widgetToReportProps} this returns a `ReportSpec`.
 *
 * `overrides` carries the per-widget bits the caller resolves at runtime (the score tile asks for a
 * single totals row; the list table asks for its selected dimension and a server page).
 */
export function widgetToReportSpec(
  widget: DashboardWidget,
  ctx: WidgetRenderContext = {},
  overrides: Partial<ReportSpec> = {},
): ReportSpec | null {
  const wire = widget.wire
  const reportId = widgetReportId(wire)
  if (reportId === undefined) return null
  const partitionName = widgetPartition(wire)
  const sortField = str(wire.sortField)
  const targetCurrency = str(ctx.targetCurrency)
  const defaultPostData = ctx.defaultPostData ?? widgetDefaultPostData(wire)

  return {
    report_id: reportId,
    ...(partitionName ? { partition_name: partitionName } : {}),
    ...(sortField ? { sort_field: sortField } : {}),
    sort_order: sortOrder(wire.sortOrder),
    filters: widgetDefaultFilters(wire),
    pagination_enabled: true,
    ...(wire.partition_only === true ? { partition_only: true } : {}),
    ...(targetCurrency ? { target_currency: targetCurrency } : {}),
    ...(defaultPostData ? { defaultPostData } : {}),
    ...overrides,
  }
}

/**
 * Every column field a widget references, in legacy's order (table dimensions, table metrics,
 * chart dimensions, chart metrics, then the chart field-role assignments).
 *
 * Ports `CustomDashboardListTable.vue:166-172`. `fieldRoleFields` is injected rather than derived
 * so this stays pure — the caller passes 08's `getSavedFieldRoleFields(wire.fieldValues)`.
 */
export function widgetColumnFields(
  wire: DashboardWidgetWire,
  fieldRoleFields: readonly string[] = [],
): string[] {
  return [
    ...new Set([
      ...strList(wire.setDimensions),
      ...strList(wire.setMetrics),
      ...strList(wire.chartDimensions),
      ...strList(wire.chartMetrics),
      ...fieldRoleFields.filter((field) => typeof field === 'string' && field.length > 0),
    ]),
  ]
}
